/**
 * Bundle loader (03 §1): manifest, events, laps, results, vectors, track and time-warp binaries, fonts,
 * and the full-race Timeline decoded from UCT2 chunks by a module worker (or in-thread as a fallback).
 *
 * `loadBundle` resolves as soon as the static files and the chunks around `startT` are decoded; the other
 * chunks keep streaming in playhead order. Streaming contract (same for the worker and in-thread paths):
 * - `ready(t)` / `readyRange(t0, t1)`: are the frames there? A miss moves the missing chunks to the front.
 * - `whenReady(t)`: resolves when they are; rejects only if one of *its* chunks failed definitively
 *   (after the pump's retries).
 * - `complete`: settles once every chunk is loaded or has failed definitively; rejects if any failed.
 *   `retry()` re-requests the failed chunks and returns (and installs) a fresh `complete`.
 * - `dispose()` / `options.signal`: stop fetching, terminate the worker, drop the arrays.
 */
import type { Bundle, CommentaryFile, FontAtlas, FontSet, LoadProgress, Manifest, RaceEvent, RadioFile, Timeline } from '../api.ts';
import type { BinaryArray, FontMetrics, FontRef, LapsFile, ResultsFile, VectorsFile } from './manifest.gen.ts';
import { viewDecoded, type DecodedArray } from './chunks.ts';
import { createTimeline } from './timeline.ts';
import { ChunkPump, joinUrl, playheadOrder, type FetchFn, type FromWorker, type PumpInit, type ToWorker } from './pump.ts';

export interface LoadOptions {
  onProgress?: (p: LoadProgress) => void;
  /** race time the player starts at: its chunks are fetched first (default 0) */
  startT?: number;
  /** fetch implementation (tests: file-backed); default globalThis.fetch */
  fetch?: FetchFn;
  /** decode in a module worker (default: when `Worker` exists); a factory overrides the worker */
  worker?: boolean | (() => Worker);
  /** md5-verify every chunk against the manifest (default false; costs ≈ 0.2 s for the race) */
  verifyMd5?: boolean;
  /** parallel chunk fetches (default 4) */
  concurrency?: number;
  /** waits before chunk re-fetch attempts (default [250, 1000] ms: 3 attempts) */
  backoffMs?: number[];
  /** atlas PNG → ImageBitmap; default createImageBitmap. Node tests pass a stub. */
  loadImage?: (blob: Blob, url: string) => Promise<ImageBitmap>;
  /** resolve only after every chunk is decoded (default false) */
  waitForAll?: boolean;
  /** fetch vectors.json (default true). H2: the Undercut view draws its own track ribbon and
   *  never shows the core vector map, so the player skips the file (0.24–2.1 MB before the first
   *  frame); `bundle.vectors` is then null and `gl.vectors` throws on use. */
  vectors?: boolean;
  /** abort loading: rejects loadBundle (if pending) and disposes the bundle */
  signal?: AbortSignal;
}

/** frames stateAt(t) reads: i−1 … i+2 (clamped), i = min(floor(t/(1/hz)), F−2) — the one frame-index rule (audit 2 fix 14), frameOf's division form */
export function framesForT(t: number, frames: number, hz = 10): [number, number] {
  const f = Math.min(Math.max((Number.isFinite(t) ? t : 0) / (1 / hz), 0), frames - 1);
  const i = Math.min(Math.floor(f), frames - 2);
  return [Math.max(i - 1, 0), Math.min(i + 2, frames - 1)];
}

function defaultFetch(): FetchFn {
  if (typeof fetch !== 'function') throw new Error('loadBundle: no global fetch; pass options.fetch');
  return (url, init) => fetch(url, init);
}

async function defaultLoadImage(blob: Blob, url: string): Promise<ImageBitmap> {
  if (typeof createImageBitmap !== 'function') throw new Error(`loadBundle: cannot decode ${url}; pass options.loadImage`);
  return createImageBitmap(blob, { premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
}

function absoluteBase(base: string): string {
  const b = base.endsWith('/') ? base : `${base}/`;
  // workers resolve relative URLs against the worker script, so hand them an absolute base
  if (typeof location !== 'undefined' && !/^[a-z][a-z0-9+.-]*:/i.test(b)) return new URL(b, location.href).href;
  return b;
}

function abortError(signal?: AbortSignal): Error {
  const r: unknown = signal?.reason;
  return r instanceof Error ? r : new DOMException('loadBundle aborted', 'AbortError');
}

/** transport to the chunk pump: a module worker or the in-thread pump */
interface Transport { post(m: ToWorker): void; close(): void }

const PENDING = 0, LOADED = 1, FAILED = 2;

/** release the full-race arrays (≈ 50 MB) on dispose; the Timeline object stays valid but empty */
function dropArrays(tl: Timeline): void {
  tl.dist = new Float64Array(0);
  tl.s = new Float32Array(0); tl.lat = new Float32Array(0); tl.hdg = new Float32Array(0);
  tl.speed = new Float32Array(0); tl.thr = new Float32Array(0); tl.rpm = new Float32Array(0);
  tl.gap = new Float32Array(0); tl.int = new Float32Array(0);
  tl.gear = new Uint8Array(0); tl.flags = new Uint16Array(0); tl.pos = new Uint8Array(0); tl.lapsdn = new Uint8Array(0);
  tl.lap = new Uint8Array(0); tl.cmp = new Uint8Array(0); tl.tyre = new Uint8Array(0); tl.stint = new Uint8Array(0);
  tl.pits = new Uint8Array(0);
  if (tl.latModel) tl.latModel = new Float32Array(0);
  tl.frames = 0;
}

export async function loadBundle(baseUrl: string, opts: LoadOptions = {}): Promise<Bundle> {
  const base = absoluteBase(baseUrl);
  const fetchFn = opts.fetch ?? defaultFetch();
  const loadImage = opts.loadImage ?? defaultLoadImage;
  const progress = opts.onProgress;
  const signal = opts.signal;
  if (signal?.aborted) throw abortError(signal);
  const staticAbort = new AbortController();

  async function get(rel: string): Promise<Response> {
    const res = await fetchFn(joinUrl(base, rel), { signal: staticAbort.signal });
    if (!res.ok) throw new Error(`loadBundle: HTTP ${String(res.status)} for ${rel}`);
    return res;
  }
  async function getJson<T>(rel: string): Promise<T> {
    return (await (await get(rel)).json()) as T;
  }
  async function getBin(a: BinaryArray): Promise<ArrayBuffer> {
    const buf = await (await get(a.file)).arrayBuffer();
    if (buf.byteLength !== a.bytes) throw new Error(`loadBundle: ${a.file} is ${String(buf.byteLength)} bytes, manifest says ${String(a.bytes)}`);
    return buf;
  }

  progress?.({ phase: 'manifest', loaded: 0, total: 1 });
  const manifest = await getJson<Manifest>('manifest.json');
  if (signal?.aborted) throw abortError(signal);
  const tm = manifest.timeline;
  const asm = createTimeline(manifest);
  const timeline: Timeline = asm.timeline;
  const nChunks = tm.files.length;
  const chunkOf = (f: number): number => Math.min(Math.floor(f / tm.chunk_frames), nChunks - 1);

  // ── timeline streaming ──────────────────────────────────────────────────────────────
  const state = new Uint8Array(nChunks);                       // PENDING / LOADED / FAILED
  const errors = new Map<number, string>();
  let nLoaded = 0, bytesLoaded = 0, disposed = false;
  interface Waiter { a: number; b: number; resolve: () => void; reject: (e: Error) => void }
  let waiters: Waiter[] = [];

  // `complete`: a fresh deferred per round (initial load, each retry())
  let resolveComplete: () => void = () => undefined;
  let rejectComplete: (e: Error) => void = () => undefined;
  let completeP: Promise<void> = Promise.resolve();
  const newComplete = (): Promise<void> => {
    completeP = new Promise<void>((res, rej) => { resolveComplete = res; rejectComplete = rej; });
    completeP.catch(() => undefined);                          // surfaced to whoever awaits it
    return completeP;
  };
  void newComplete();

  let transport: Transport | null = null;
  const post = (m: ToWorker): void => { transport?.post(m); };

  const missingIn = (a: number, b: number): boolean => {
    for (let i = a; i <= b; i++) if (state[i] !== LOADED) return true;
    return false;
  };
  const failedIn = (a: number, b: number): string | undefined => {
    for (let i = a; i <= b; i++) if (state[i] === FAILED) return errors.get(i) ?? 'failed';
    return undefined;
  };
  /** move pending chunks a..b to the front (a ends up first) */
  const prioritize = (a: number, b: number): void => {
    for (let i = b; i >= a; i--) if (state[i] === PENDING) post({ type: 'prioritize', i });
  };
  const chunkRange = (t0: number, t1 = t0): [number, number] => {
    const lo = Math.min(t0, t1), hi = Math.max(t0, t1);
    return [chunkOf(framesForT(lo, timeline.frames, timeline.hz)[0]), chunkOf(framesForT(hi, timeline.frames, timeline.hz)[1])];
  };
  const settleWaiters = (): void => {
    if (waiters.length === 0) return;
    const keep: Waiter[] = [];
    for (const w of waiters) {
      const err = failedIn(w.a, w.b);
      if (err !== undefined) w.reject(new Error(`timeline chunk failed: ${err}`));
      else if (!missingIn(w.a, w.b)) w.resolve();
      else keep.push(w);
    }
    waiters = keep;
  };
  const checkComplete = (): void => {
    if (nLoaded === nChunks) {
      resolveComplete();
      transport?.close();                                     // done: free the worker
      transport = null;
      return;
    }
    for (let i = 0; i < nChunks; i++) if (state[i] === PENDING) return;
    rejectComplete(new Error(`timeline: ${String(errors.size)} chunk(s) failed: ${[...errors].map(([i, m]) => `${String(i)} (${m})`).join(', ')}`));
  };

  const onMessage = (msg: FromWorker): void => {
    if (disposed) return;
    if (msg.type === 'error') {
      if (state[msg.i] === PENDING) {
        state[msg.i] = FAILED;
        errors.set(msg.i, msg.message);
      }
      settleWaiters();
      checkComplete();
      return;
    }
    if (state[msg.i] === LOADED) return;
    const fields = new Map<number, DecodedArray>();
    for (const f of msg.fields) fields.set(f.id, viewDecoded(f.dtype, f.buffer));
    asm.apply(msg.f0, msg.n, fields);
    state[msg.i] = LOADED;
    errors.delete(msg.i);
    nLoaded++;
    bytesLoaded += msg.bytes;
    progress?.({ phase: 'timeline', loaded: bytesLoaded, total: tm.bytes_total });
    settleWaiters();
    checkComplete();
  };

  const startT = opts.startT ?? 0;
  const init: PumpInit = {
    type: 'init',
    baseUrl: base,
    files: tm.files,
    scales: [...asm.scales],
    nCars: tm.n_cars,
    order: playheadOrder(nChunks, chunkRange(startT)[0]),
    verifyMd5: opts.verifyMd5 ?? false,
    concurrency: opts.concurrency ?? 4,
    ...(opts.backoffMs ? { backoffMs: opts.backoffMs } : {}),
  };
  const useWorker = opts.worker !== false && (typeof opts.worker === 'function' || typeof Worker !== 'undefined');
  if (useWorker) {
    const worker = typeof opts.worker === 'function'
      ? opts.worker()
      : new Worker(new URL('./worker.ts', import.meta.url), { type: 'module', name: 'undercut-timeline' });
    worker.onmessage = (ev: MessageEvent<FromWorker>): void => { onMessage(ev.data); };
    worker.onerror = (ev: ErrorEvent): void => {
      // the worker itself died: every pending chunk fails definitively (retry() needs a new bundle)
      for (let i = 0; i < nChunks; i++) {
        if (state[i] === PENDING) { state[i] = FAILED; errors.set(i, `worker error: ${ev.message}`); }
      }
      transport?.close();
      transport = null;
      settleWaiters();
      checkComplete();
    };
    transport = {
      post: (m) => { worker.postMessage(m); },
      close: () => { worker.postMessage({ type: 'stop' } satisfies ToWorker); worker.terminate(); },
    };
    worker.postMessage(init);
  } else {
    const pump = new ChunkPump(init, fetchFn, (m) => { onMessage(m); });
    transport = {
      post: (m) => {
        if (m.type === 'prioritize') pump.prioritize(m.i);
        else if (m.type === 'retry') pump.retry(m.i);
        else if (m.type === 'stop') pump.stop();
      },
      close: () => { pump.stop(); },
    };
    pump.start();
  }

  const ready = (t: number): boolean => readyRange(t, t);
  function readyRange(t0: number, t1: number): boolean {
    const [a, b] = chunkRange(t0, t1);
    if (!missingIn(a, b)) return true;
    prioritize(a, b);
    return false;
  }
  const whenReady = (t: number): Promise<void> => {
    if (disposed) return Promise.reject(new Error('bundle disposed'));
    const [a, b] = chunkRange(t);
    if (!missingIn(a, b)) return Promise.resolve();
    const err = failedIn(a, b);
    if (err !== undefined) return Promise.reject(new Error(`timeline chunk failed: ${err}`));
    prioritize(a, b);
    return new Promise<void>((resolve, reject) => { waiters.push({ a, b, resolve, reject }); });
  };
  const retry = (): Promise<void> => {
    if (disposed) return Promise.reject(new Error('bundle disposed'));
    if (nLoaded === nChunks) return completeP;
    const failed = [...errors.keys()].filter((i) => state[i] === FAILED);
    if (failed.length === 0) return completeP;               // still streaming: the current round stands
    if (!transport) return Promise.reject(new Error('timeline worker is gone; reload the bundle'));
    const p = newComplete();
    for (const i of failed) {
      state[i] = PENDING;
      errors.delete(i);
      post({ type: 'retry', i });
    }
    return p;
  };
  const dispose = (reason?: Error): void => {
    if (disposed) return;
    disposed = true;
    staticAbort.abort();
    transport?.close();
    transport = null;
    const err = reason ?? new Error('bundle disposed');
    for (const w of waiters) w.reject(err);
    waiters = [];
    rejectComplete(err);
    dropArrays(timeline);
  };
  const onAbort = (): void => { dispose(abortError(signal)); };
  signal?.addEventListener('abort', onAbort, { once: true });

  // ── static files (in parallel with the timeline) ─────────────────────────────────────
  let assetsDone = 0;
  const ASSETS = 9;
  const tick = <T>(p: Promise<T>): Promise<T> => p.then((v) => {
    assetsDone++;
    progress?.({ phase: 'assets', loaded: assetsDone, total: ASSETS });
    return v;
  });
  const loadFont = async (ref: FontRef): Promise<FontAtlas> => {
    const metrics = await getJson<FontMetrics>(ref.metrics);
    const blob = await (await get(ref.atlas)).blob();
    return { metrics, atlas: await loadImage(blob, joinUrl(base, ref.atlas)) };
  };
  let statics;
  try {
    statics = await Promise.all([
      tick(getJson<RaceEvent[]>(manifest.events.file)),
      tick(getJson<LapsFile>('laps.json')),
      tick(getJson<ResultsFile>('results.json')),
      // H2: skipped when the caller opts out — the tick still counts it, so progress stays 0..9
      tick(opts.vectors === false ? Promise.resolve(null) : getJson<VectorsFile>('vectors.json')),
      tick(getBin(manifest.track.ref_xy)),
      tick(getBin(manifest.track.ref_s)),
      tick(getBin(manifest.track.ref_nrm)),
      tick(Promise.all([getBin(manifest.timewarp.race_t), getBin(manifest.timewarp.speed)])),
      tick(Promise.all([loadFont(manifest.fonts['5x7']), loadFont(manifest.fonts['3x5'])])),
    ]);
  } catch (e) {
    // a missing static file (or an abort) stops chunk fetching too
    const err = signal?.aborted ? abortError(signal) : e instanceof Error ? e : new Error(String(e));
    dispose(err);
    signal?.removeEventListener('abort', onAbort);
    throw err;
  }
  const [events, laps, results, vectors, refXY, refS, refNrm, warp, fonts] = statics;
  const fontSet: FontSet = { '5x7': fonts[0], '3x5': fonts[1] };

  const precomputeCache = new Map<string, Promise<unknown>>();
  function precompute<T>(name: string): Promise<T> {
    let p = precomputeCache.get(name);
    if (!p) {
      const rel = `precompute/${name}`;
      p = name.endsWith('.json') ? getJson<unknown>(rel) : get(rel).then((r) => r.arrayBuffer());
      precomputeCache.set(name, p);
      p.catch(() => precomputeCache.delete(name));
    }
    return p as Promise<T>;
  }

  // optional race-audio files: only fetched when the manifest lists them; a failure just means "none"
  const optionalJson = <T>(kind: string): Promise<T | null> => {
    const f = manifest.files.find((x) => x.kind === kind);
    return f ? getJson<T>(f.path).catch(() => null) : Promise.resolve(null);
  };

  const bundle: Bundle = {
    baseUrl: base,
    radio: optionalJson<RadioFile>('radio'),
    commentary: optionalJson<CommentaryFile>('commentary'),
    manifest,
    events,
    laps,
    results,
    timeline,
    warpRaceT: new Float64Array(warp[0]),
    warpSpeed: new Float64Array(warp[1]),
    track: { refXY: new Float32Array(refXY), refS: new Float32Array(refS), refNrm: new Float32Array(refNrm) },
    vectors,
    precompute,
    fonts: fontSet,
    ready,
    readyRange,
    whenReady,
    get complete(): Promise<void> { return completeP; },
    retry,
    dispose: () => { dispose(); },
  };
  try {
    await whenReady(startT);
    if (opts.waitForAll) await completeP;
  } catch (e) {
    // a failed startup (or an abort) stops chunk fetching too: nothing else reads these arrays
    // (mid-session chunk failures keep the transport instead — bundle.retry() owns those)
    const err = signal?.aborted ? abortError(signal) : e instanceof Error ? e : new Error(String(e));
    dispose(err);
    signal?.removeEventListener('abort', onAbort);
    throw err;
  }
  return bundle;
}
